/**
 * Telemetry payload for an event discarded by an event processor.
 *
 * Mirrors `IgnoredDropInfo` field-for-field where the concepts overlap, so the
 * two halves land in one queryable `sentry_event_dropped` table rather than in
 * two shapes that have to be unioned by hand.
 */
export interface ProcessorDropInfo {
  /** Integration name (`Dedupe`, `InboundFilters`) or `processor-<index>`. */
  processor: string;
  /** `error` | `transaction` | … — Sentry's own event type. */
  eventType: string;
  /** First 120 chars of the event message, PII-redacted. */
  messagePrefix: string;
  /** `TypeError`, `ChunkLoadError`, … — empty when the event carries none. */
  exceptionType: string;
  /** Request route — normalized, no query string, no opaque segments. */
  route: string;
  /** `production` | `preview` | `development` | … */
  environment: string;
  /**
   * True on the LAST row a signature — or the whole pageload — is allowed to
   * emit. Read it as "the counts at and below this row are FLOORS, not totals".
   *
   * Set by EITHER ceiling, and that is the point. The per-signature cap is the
   * one that actually fires in a BAL-959 burst: a single looping error caps at
   * 20 rows while `totalReports` never comes near the 100 pageload ceiling. A
   * marker derived from the pageload ceiling alone left all 20 of those rows
   * reading `false`, which downstream reads as "20 drops, fully attributed" —
   * reproducing one level down the exact failure this field exists to prevent:
   * a burst that looks fully attributed but is not.
   */
  budgetExhausted: boolean;
  /**
   * True when the PER-SIGNATURE cap is the ceiling that fired.
   *
   * Separates the two truncations, which demand different readings. This one
   * means "this signature stopped being counted; every OTHER signature on the
   * pageload is still complete". `budgetExhausted` without it means the
   * pageload itself stopped, so nothing after that row was recorded at all.
   * A query that reads only `budgetExhausted` is never WRONG — it just cannot
   * tell the two apart, which is the safe direction for a flag to fail in.
   */
  signatureExhausted: boolean;
}

export type ProcessorDropHandler = (drop: ProcessorDropInfo) => void;

/**
 * Emitted once per pageload, on the first ERROR event to enter the chain.
 *
 * This is the liveness half, and it is the whole reason a future zero-row
 * result can be trusted. Without it, "the sink returned no rows" has two
 * indistinguishable causes: nothing was dropped, or the sink was never wired.
 * That ambiguity is what made four consecutive bursts unattributable. With it,
 * `armed = 0` means the instrumentation is not deployed or not reached, and
 * `armed > 0 AND drops = 0` means the sink ran and every processor it COVERS
 * kept the event.
 *
 * COVERS is the load-bearing word, and it is why there are five counts rather
 * than two. Each field exists because a reading that was available without it
 * was wrong:
 *
 *   - `processorCount` + `scopeProcessorCount` — what this event was covered
 *     for. Both count wrappers, not array entries.
 *   - `wrappedThisEventCount` — what was in the chain and NOT covered on this
 *     event. Non-zero on a fork-local processor, on every event it ever sees.
 *   - `scopesRequested` vs `scopesResolved` — separates "the scopes were read
 *     and hold no processors" from "the scopes could not be read at all".
 *
 * So the strong statement a zero-row result supports is bounded: the drop came
 * from neither the client list nor from any scope processor that was covered.
 * It says nothing about a fork-local or `finalScope` processor — see the
 * module docstring's RESIDUAL GAPS — which is exactly what
 * `wrappedThisEventCount` is there to make visible instead of assumed away.
 */
export interface SinkArmedInfo {
  /**
   * CLIENT event processors COVERED for this event: already wrapped when
   * `prepareEvent` spread the chain. Excludes the beacon, which is the
   * instrumentation rather than something it watches.
   *
   * Covered, not present. A processor pushed onto the client list after init
   * IS wrapped by the beacon's re-scan, but that wrap lands on the array the
   * NEXT event runs — so on this event it is unwrapped, and counting it here
   * would claim coverage the sink does not have. It appears in
   * `wrappedThisEventCount` instead and moves into this count from the next
   * event on.
   *
   * The array length is deliberately not the number: it also counts
   * non-function entries, which are never wrapped and never drop anything.
   */
  processorCount: number;
  /**
   * SCOPE-level event processors COVERED for this event, across the global,
   * isolation and current scopes, deduplicated by processor identity.
   *
   * Deliberately a SECOND field rather than a widening of `processorCount`.
   * The two arrays fail independently — a client-list regression and a
   * scope-list regression have different causes and different fixes — and one
   * summed number cannot tell them apart. Splitting them also keeps
   * `processor_count` meaning the same thing it meant in every PostHog row
   * written before scope coverage existed, so the historical series stays
   * comparable instead of silently changing units mid-flight.
   *
   * Deduplicated because one scope object reachable through two resolvers is
   * ONE processor in the chain, not two; summing `list.length` across the
   * three sources counted it once per source.
   *
   * `0` here is a real reading only alongside a non-zero `scopesResolved` — it
   * then means the scopes were read and held no covered processors. With
   * `scopesResolved: 0` it means nothing was read at all, which is a different
   * finding with a different fix.
   */
  scopeProcessorCount: number;
  /**
   * Processors — client and scope alike — wrapped for the FIRST time during
   * this event, and therefore NOT covered on it.
   *
   * A long-lived array drains this to zero: whatever is wrapped now is covered
   * from the next event on. An ephemeral one never does. A processor added
   * inside `withScope` lives for exactly one event, so it is wrapped for the
   * first time every time and is counted here every time — which is the only
   * honest place to count it, since the wrap lands on an array the callback
   * discards. A `wrappedThisEventCount` that stays non-zero across pageloads
   * therefore reads as "fork-local processors are in play", not as "the sink
   * is still warming up".
   */
  wrappedThisEventCount: number;
  /**
   * Scope sources this install accounts for.
   *
   * Not simply `scopes.length` as passed by the caller: an install whose scope
   * processors are already owned by an earlier sink accounts for none of them,
   * and reports `0` here rather than claiming sources whose drops report
   * somewhere else.
   */
  scopesRequested: number;
  /**
   * How many of those sources yielded a processor array on this event.
   *
   * Exists because `scopesResolved < scopesRequested` and
   * `scopeProcessorCount: 0` are different findings that used to look
   * identical. A source is skipped when its resolver throws, returns nothing,
   * exposes no `getScopeData`, or hands back a non-array — four failure modes
   * that all reduced to a silent zero. A `getScopeData` renamed upstream would
   * have degraded the whole scope half to no coverage while the beacon still
   * armed and still reported `scopeProcessorCount: 0`, which reads as "the
   * scopes are empty". `3 requested, 0 resolved` cannot be read that way.
   */
  scopesResolved: number;
  environment: string;
}

export type SinkArmedHandler = (info: SinkArmedInfo) => void;

/**
 * Cap on reports per processor+signature per pageload.
 *
 * On the server and edge there is no pageload: the same cap applies per
 * `budgetWindowMs` window per process instead (BAL-1038).
 *
 * Matches `MAX_DROP_REPORTS_PER_PATTERN` in `sentry.client.config.ts` on
 * purpose: the two halves write the same PostHog event, so a burst must not be
 * throttled generously on one path and tightly on the other. The bound is what
 * keeps the sink from becoming the volume problem it exists to measure — the
 * 2026-08-19 burst was ~1700 discards in one hour, and this caps a single
 * pathological tab's contribution at 20 rows per distinct drop signature.
 */
export const MAX_PROCESSOR_DROP_REPORTS_PER_KEY = 20;

/**
 * Hard ceiling on reports per pageload, across ALL signatures.
 *
 * On the server and edge it is the ceiling per `budgetWindowMs` window per
 * process instead (BAL-1038).
 *
 * The per-signature cap alone does NOT bound total volume, and assuming it
 * does is the easy mistake here. It bounds one repeated error; a burst of
 * 1700 DISTINCT messages has 1700 distinct signatures and would have produced
 * ~1700 PostHog writes — turning a diagnostic into the same quota problem it
 * exists to diagnose. This ceiling is what actually holds the guarantee.
 *
 * 100 leaves room for five fully-attributed signatures at the per-key cap,
 * which is far more than is needed to name a culprit, while bounding a
 * pathological tab. It also bounds the throttle Map: entries are only created
 * for reports that are actually emitted, so the Map cannot exceed this.
 */
export const MAX_PROCESSOR_DROP_REPORTS_PER_PAGELOAD = 100;

/** Max chars of exception type forwarded. Class names are short by nature. */
export const EXCEPTION_TYPE_LENGTH = 60;

/** Marker so a processor is never double-wrapped across lazy re-scans. */
export const WRAPPED_FLAG = '__balleeDropInstrumented';

/** `id` of the head-of-chain beacon. Also the double-install sentinel. */
export const BEACON_ID = 'BalleeDropSinkBeacon';

export interface MinimalEvent {
  type?: string;
  message?: string;
  exception?: { values?: Array<{ type?: string; value?: string }> };
  request?: { url?: string };
  tags?: Record<string, unknown>;
  sdkProcessingMetadata?: { normalizedRequest?: { url?: string } };
}

/**
 * `undefined` is in this union deliberately: the SDK's own chain halts on any
 * falsy return, so a processor CAN drop an event without returning `null`, and
 * a type that denied it would make the guard below look redundant.
 */
export type ProcessorResult =
  | MinimalEvent
  | null
  | undefined
  | PromiseLike<MinimalEvent | null | undefined>;

export interface EventProcessorLike {
  (event: MinimalEvent, hint: unknown): ProcessorResult;
  id?: string;
  [WRAPPED_FLAG]?: boolean;
}

export interface ClientLike {
  getEventProcessors?: () => EventProcessorLike[];
}

/**
 * The three scopes `getCombinedScopeData` merges, in the order it merges them
 * (`utils/scopeData.js:67-72`). The label is part of the reported processor
 * name, so a drop says WHICH scope it came from, not merely that it came from
 * one.
 */
export type ScopeKind = 'global' | 'isolation' | 'current';

/**
 * A scope whose processors join the chain, supplied as a RESOLVER.
 *
 * A resolver, not a scope instance, because `getCurrentScope()` returns a
 * different object inside every `withScope` and `Scope.clone` copies
 * `_eventProcessors` into a new array — so a scope captured at install time
 * stops being the one the next event runs against.
 */
export interface ScopeSource {
  kind: ScopeKind;
  resolve: () => unknown;
}

export interface ScopeLike {
  getScopeData?: () => { eventProcessors?: unknown } | undefined;
}

/**
 * What an install call actually did.
 *
 * A boolean could not say this. It conflated "this call installed the sink"
 * with "an earlier call already covers this client", and the second answer was
 * returned in a case where the call had installed NOTHING — see the scope
 * ownership note in `instrumentEventProcessorDrops`. A caller that logs or
 * asserts on the outcome needs the two apart.
 *
 *   - `installed`       — this call unshifted the beacon and wrapped the chain.
 *   - `already-covered` — this exact client already carries a beacon; nothing
 *                         was changed, and drops still report to the first sink.
 *   - `not-wired`       — the client exposed no `getEventProcessors`. Read this
 *                         as "no telemetry", NEVER as "nothing was dropped".
 */
export type ProcessorDropInstallOutcome =
  | 'installed'
  | 'already-covered'
  | 'not-wired';

export interface ResolvedScope {
  kind: ScopeKind;
  list: EventProcessorLike[];
}
